import { useCallback, type Dispatch, type SetStateAction } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { deleteChallengeById } from "../../challenges/services/challengeAdminService";
import type {
  ChallengeDetail,
  ChallengeHistoryItem,
} from "../../challenges/types/challengeAdmin";

type UseAdminChallengeDeletionInput = {
  challengeDetail: ChallengeDetail | null;
  challengeIdFromRoute?: number;
  confirmDeleteChallengeTarget: ChallengeHistoryItem | null;
  goToAdminListPage: () => void;
  loadHistory: () => Promise<void>;
  selectedChallengeId: number | null;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeRoundsJson: Dispatch<SetStateAction<string>>;
  setConfirmDeleteChallengeError: Dispatch<SetStateAction<string>>;
  setConfirmDeleteChallengeLoading: Dispatch<SetStateAction<boolean>>;
  setConfirmDeleteChallengeTarget: Dispatch<
    SetStateAction<ChallengeHistoryItem | null>
  >;
  setSelectedChallengeId: Dispatch<SetStateAction<number | null>>;
  setShowChallengeModal: Dispatch<SetStateAction<boolean>>;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

export function useAdminChallengeDeletion({
  challengeDetail,
  challengeIdFromRoute,
  confirmDeleteChallengeTarget,
  goToAdminListPage,
  loadHistory,
  selectedChallengeId,
  setChallengeDetail,
  setChallengeRoundsJson,
  setConfirmDeleteChallengeError,
  setConfirmDeleteChallengeLoading,
  setConfirmDeleteChallengeTarget,
  setSelectedChallengeId,
  setShowChallengeModal,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminChallengeDeletionInput) {
  const t = siteTexts.admin;

  const clearSelectedChallenge = useCallback(() => {
    setChallengeDetail(null);
    setSelectedChallengeId(null);
    setChallengeRoundsJson("[]");
    setShowChallengeModal(false);
    if (challengeIdFromRoute) {
      goToAdminListPage();
    }
  }, [
    challengeIdFromRoute,
    goToAdminListPage,
    setChallengeDetail,
    setChallengeRoundsJson,
    setSelectedChallengeId,
    setShowChallengeModal,
  ]);

  const onDeleteChallenge = useCallback(async () => {
    if (!token || !challengeDetail) return;
    const confirmed = window.confirm(t.confirms.removeChallenge);
    if (!confirmed) return;

    try {
      await deleteChallengeById(token, challengeDetail.id);
      clearSelectedChallenge();
      showSuccessToast(t.toasts.challengeDeleteSuccess);
      await loadHistory();
    } catch (_err) {
      showErrorToast(t.toasts.challengeDeleteError);
    }
  }, [
    challengeDetail,
    clearSelectedChallenge,
    loadHistory,
    showErrorToast,
    showSuccessToast,
    t.confirms.removeChallenge,
    t.toasts.challengeDeleteError,
    t.toasts.challengeDeleteSuccess,
    token,
  ]);

  const onRequestDeleteChallengeFromList = useCallback(
    (item: ChallengeHistoryItem) => {
      setConfirmDeleteChallengeError("");
      setConfirmDeleteChallengeTarget(item);
    },
    [setConfirmDeleteChallengeError, setConfirmDeleteChallengeTarget],
  );

  const onConfirmDeleteChallengeFromList = useCallback(async () => {
    if (!token || !confirmDeleteChallengeTarget) return;
    setConfirmDeleteChallengeError("");
    setConfirmDeleteChallengeLoading(true);
    try {
      await deleteChallengeById(token, confirmDeleteChallengeTarget.id);
      if (selectedChallengeId === confirmDeleteChallengeTarget.id) {
        clearSelectedChallenge();
      }
      setConfirmDeleteChallengeTarget(null);
      showSuccessToast(t.toasts.challengeDeleteSuccess);
      await loadHistory();
    } catch (err) {
      setConfirmDeleteChallengeError(
        err instanceof Error ? err.message : t.toasts.challengeDeleteError,
      );
    } finally {
      setConfirmDeleteChallengeLoading(false);
    }
  }, [
    clearSelectedChallenge,
    confirmDeleteChallengeTarget,
    loadHistory,
    selectedChallengeId,
    setConfirmDeleteChallengeError,
    setConfirmDeleteChallengeLoading,
    setConfirmDeleteChallengeTarget,
    showSuccessToast,
    t.toasts.challengeDeleteError,
    t.toasts.challengeDeleteSuccess,
    token,
  ]);

  return {
    onConfirmDeleteChallengeFromList,
    onDeleteChallenge,
    onRequestDeleteChallengeFromList,
  };
}
